import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DataGrid } from '@mui/x-data-grid'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { useTranslation } from 'react-i18next'
import { enqueueSnackbar } from 'notistack'
import Confirm from 'components/Dialog'
import Filters from 'components/Filters'
import NoRecords from 'components/NoRecords'
import { useSession } from 'components/Providers'
import { getAllStudents, getGradeLevelAndSections } from 'store/lookup'
import { generate } from 'store/scheduling/report-card'
import { resolveCurrentMarkingPeriodId } from 'utils'
import { toPngDataUrl } from 'utils/image'
import { LAYOUT_OPTIONS, LOGO_SIZE, buildReportCardPdf } from './pdf'
import { openPdfWindow } from './pdfWindow'

const slug = text =>
  String(text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

export default function ReportCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const { academicYear, school } = useSession()

  const markingPeriods = useSelector(state => state.session.academicYear?.markingPeriods)
  const gradeLevelAndSections = useSelector(state => state.lookup.gradeLevelAndSections)
  const allStudents = useSelector(state => state.lookup.allStudents?.rows)

  const [query, setQuery] = useState(() => ({
    markingPeriodId: resolveCurrentMarkingPeriodId(markingPeriods),
    status: 'active',
  }))
  const [selectedIds, setSelectedIds] = useState([])
  const [generating, setGenerating] = useState(false)
  const [confirmAll, setConfirmAll] = useState(false)
  const [notifyParents, setNotifyParents] = useState(false)
  const [addToDocuments, setAddToDocuments] = useState(false)
  const [layout, setLayout] = useState('classic')

  useEffect(() => {
    gradeLevelAndSections || dispatch(getGradeLevelAndSections())
    allStudents || dispatch(getAllStudents({ limit: 1000 }))
  }, [dispatch, Boolean(gradeLevelAndSections), Boolean(allStudents)])

  useEffect(() => {
    if (!markingPeriods?.some(period => sameId(period.id, query.markingPeriodId))) {
      setQuery(prev => ({ ...prev, markingPeriodId: resolveCurrentMarkingPeriodId(markingPeriods) }))
    }
  }, [markingPeriods])

  const gradeOptions = useMemo(
    () =>
      gradeLevelAndSections?.flatMap(grade => [
        { value: `L:${grade.id}`, label: `${t('Grade')} ${grade.id}`, gradeLevelId: grade.id, gradeSectionId: null },
        ...(grade.section ?? []).map(section => ({
          value: `S:${section.id}`,
          label: `${t('Section')} ${section.gradeSection}`,
          gradeLevelId: grade.id,
          gradeSectionId: section.id,
        })),
      ]) ?? [],
    [gradeLevelAndSections, t],
  )
  const scope = gradeOptions.find(option => option.value === query.grade) ?? null

  const filtersConfig = useMemo(
    () => [
      {
        key: 'markingPeriodId',
        label: t('Marking Period'),
        type: 'autocomplete',
        options: (markingPeriods ?? []).map(period => ({ value: period.id, label: period.name })),
        disableClearable: true,
        gridProps: { xs: 12, sm: 6, md: 3 },
      },
      {
        key: 'grade',
        label: t('Grade Level / Section'),
        type: 'autocomplete',
        options: gradeOptions,
        gridProps: { xs: 12, sm: 6, md: 3 },
        renderOption: ({ style, ...props }, option) => (
          <li {...props} key={option.value} style={{ ...style, paddingLeft: option.gradeSectionId ? 40 : 16 }}>
            {option.label}
          </li>
        ),
      },
      {
        key: 'status',
        label: t('Status'),
        type: 'autocomplete',
        options: [
          { value: 'active', label: t('Active') },
          { value: 'inactive', label: t('Inactive') },
        ],
        gridProps: { xs: 12, sm: 6, md: 2 },
      },
      {
        key: 'name',
        label: t('Search student'),
        type: 'text',
        gridProps: { xs: 12, sm: 6, md: 4 },
      },
    ],
    [t, markingPeriods, gradeOptions],
  )

  const inScope = student => {
    const enrollment = student.academicYearEnrollment
    if (scope?.gradeSectionId) return sameId(enrollment?.gradeSectionId, scope.gradeSectionId)
    if (scope?.gradeLevelId) return sameId(enrollment?.gradeLevelId, scope.gradeLevelId)
    return true
  }

  const rows = useMemo(() => {
    const words = String(query.name ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean)
    return (allStudents ?? []).filter(student => {
      if (!inScope(student)) return false
      if (query.status === 'active' && !student.isActive) return false
      if (query.status === 'inactive' && student.isActive) return false
      const name = `${student.person?.firstName ?? ''} ${student.person?.lastName ?? ''}`.toLowerCase()
      return words.every(word => name.includes(word))
    })
  }, [allStudents, scope, query.status, query.name])

  // Without picked students the API covers the active students of the grade / section
  const scopeCount = useMemo(
    () => (allStudents ?? []).filter(student => student.isActive && inScope(student)).length,
    [allStudents, scope],
  )

  const columns = [
    {
      field: 'name',
      headerName: t('Student Name'),
      flex: 1.5,
      minWidth: 180,
      valueGetter: (value, row) => `${row.person?.lastName ?? ''}, ${row.person?.firstName ?? ''}`,
    },
    {
      field: 'grade',
      headerName: t('Grade'),
      flex: 0.6,
      minWidth: 90,
      valueGetter: (value, row) => row.academicYearEnrollment?.gradeLevelId ?? '',
    },
    {
      field: 'section',
      headerName: t('Section'),
      flex: 0.6,
      minWidth: 90,
      valueGetter: (value, row) => row.academicYearEnrollment?.section?.gradeSection ?? '',
    },
    {
      field: 'isActive',
      headerName: t('Status'),
      flex: 0.6,
      minWidth: 110,
      renderCell: ({ value }) => (
        <Chip size="small" variant="outlined" color={value ? 'success' : 'default'} label={value ? t('Active') : t('Inactive')} />
      ),
    },
  ]

  const target = selectedIds.length
    ? t('{{count}} selected students', { count: selectedIds.length })
    : scope
      ? scope.gradeSectionId
        ? `${t('Grade')} ${scope.gradeLevelId} · ${scope.label}`
        : scope.label
      : t('All students')

  const generateLabel = selectedIds.length
    ? t('Generate PDF ({{count}})', { count: selectedIds.length })
    : t('Generate PDF ({{count}})', { scope: target, count: scopeCount })

  const run = async () => {
    setConfirmAll(false)
    const markingPeriod = markingPeriods?.find(period => sameId(period.id, query.markingPeriodId))
    const fileName = `report-cards-${slug(markingPeriod?.name)}-${slug(target)}.pdf`
    const preview = openPdfWindow({
      title: t('Report Card'),
      loading: t('Generating…'),
      saveLabel: t('Save PDF'),
      color: theme.palette.primary.main,
    })

    setGenerating(true)
    try {
      const payload = { markingPeriodId: Number(query.markingPeriodId) }
      if (selectedIds.length) payload.schoolEnrollmentIds = selectedIds.map(Number)
      else {
        if (scope?.gradeSectionId) payload.gradeSectionId = Number(scope.gradeSectionId)
        else if (scope?.gradeLevelId) payload.gradeLevelId = String(scope.gradeLevelId)
      }

      const res = await dispatch(generate(payload))
      const cards = res.error ? null : res.payload ?? []
      if (!cards?.length) {
        preview.close()
        if (cards) enqueueSnackbar(t('No report cards to generate'), { variant: 'info' })
        return
      }

      const logo = await toPngDataUrl(theme.logos?.color || theme.logos?.circle, LOGO_SIZE)
      const doc = buildReportCardPdf({
        cards,
        layout,
        markingPeriodId: Number(query.markingPeriodId),
        logo,
        schoolName: school?.name,
        academicYearName: academicYear?.name,
        primaryColor: theme.palette.primary.main,
        t,
      })
      preview.show(doc, fileName)

      if (addToDocuments) handleAddToDocuments(cards)
      if (notifyParents) handleNotifyParents(cards)
    } catch (err) {
      preview.close()
      enqueueSnackbar(t('The report cards could not be generated'), { variant: 'error' })
    } finally {
      setGenerating(false)
    }
  }

  // TODO: document add to portfolio
  const handleAddToDocuments = cards =>
    enqueueSnackbar(t('Adding {{count}} report cards to student documents will be available soon', { count: cards.length }), {
      variant: 'info',
    })

  //TODO: notify parents 
  const handleNotifyParents = cards =>
    enqueueSnackbar(t('Notifying parents of {{count}} report cards will be available soon', { count: cards.length }), {
      variant: 'info',
    })

  const onGenerate = () => (!selectedIds.length && !scope ? setConfirmAll(true) : run())

  return (
    <Box>
      <Filters
        filtersConfig={filtersConfig}
        values={query}
        onChange={next => setQuery(prev => ({ ...next, markingPeriodId: next.markingPeriodId ?? prev.markingPeriodId }))}
        gridContainerProps={{ sx: { p: 1, pt: 2, width: '100%', flexWrap: 'wrap' } }}
      />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, pb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {selectedIds.length
            ? t('{{count}} selected', { count: selectedIds.length })
            : t('Select the students you want to generate report cards for. To select all students, tick the checkbox in the header.')}
        </Typography>
        {selectedIds.length > 0 && (
          <Button size="small" onClick={() => setSelectedIds([])}>
            {t('Clear')}
          </Button>
        )}
        <TextField
          select
          size="small"
          label={t('Design')}
          value={layout}
          onChange={event => setLayout(event.target.value)}
          sx={{ minWidth: 130 }}
        >
          {LAYOUT_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Checkbox size="small" checked={addToDocuments} onChange={event => setAddToDocuments(event.target.checked)} />}
          label={<Typography variant="body2">{t('Add to student documents')}</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={notifyParents} onChange={event => setNotifyParents(event.target.checked)} />}
          label={<Typography variant="body2">{t('Notify parents')}</Typography>}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
          disabled={generating || !query.markingPeriodId || !selectedIds.length}
          onClick={onGenerate}
        >
          {generating ? t('Generating…') : selectedIds.length ? generateLabel : t('Generate PDF')}
        </Button>
      </Stack>

      <Box px={1}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={row => row.schoolEnrollmentId}
          loading={!allStudents}
          checkboxSelection
          keepNonExistentRowsSelected
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={setSelectedIds}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnMenu
          slots={{ noRowsOverlay: NoRecords }}
          slotProps={{ loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' } }}
          sx={{
            height: 'calc(100vh - 355px)',
            minHeight: 360,
            overflow: 'auto',
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
        />
      </Box>

      <Confirm
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        onConfirm={run}
        dialogTitle={t('Generate Report Cards')}
        message={t('No grade, section or students are selected. Generate report cards for all {{count}} students?', {
          count: scopeCount,
        })}
      />
    </Box>
  )
}
